import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import {
  hasAuthSession, setAuthTokens, clearAuthTokens, getRefreshToken,
  apiLogin, apiLogout, apiGetMe, apiGetProjects,
  apiCreateProject, apiUpdateProject, apiDeleteProject,
  apiCreateTask, apiUpdateTask, apiDeleteTask,
  apiCreateNote, apiDeleteNote,
} from '../api/client';

const AppContext = createContext(null);

/* ── helpers ──────────────────────────────────────────── */
const initials = (name = '') =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(w => w[0].toUpperCase())
    .join('') || '?';

const formatNoteDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
};

const decorateNote = (note) => ({
  id: note.id,
  text: note.text,
  date: note.date ?? formatNoteDate(note.createdAt),
});

const decorateTask = (task) => ({
  id: task.id,
  title: task.title,
  desc: task.desc ?? '',
  status: task.status,
  priority: task.priority,
  createdAt: task.createdAt ?? null,
  updatedAt: task.updatedAt ?? task.createdAt ?? null,
  createdBy: task.createdBy ?? null,
  createdByName: task.creator?.name ?? task.createdByName ?? null,
});

const decorateProject = (project) => ({
  id: project.id,
  title: project.title,
  description: project.description ?? '',
  color: project.color,
  stagingUrl: project.stagingUrl ?? '',
  productionUrl: project.productionUrl ?? '',
  parentId: project.parentId ?? null,
  createdAt:
    typeof project.createdAt === 'string'
      ? project.createdAt.slice(0, 10)
      : new Date(project.createdAt).toISOString().slice(0, 10),
  tasks: (project.tasks || []).map(decorateTask),
  notes: (project.notes || []).map(decorateNote),
  children: (project.children || []).map(decorateProject),
});

const decorateUser = (user) => ({
  id: user.id,
  name: user.name,
  email: user.email,
  avatar: initials(user.name),
});

export const findProjectById = (projects, id) => {
  for (const p of projects) {
    if (p.id === id) return p;
    const child = p.children?.find(c => c.id === id);
    if (child) return child;
  }
  return null;
};

export const getAllTasks = (project) => {
  const own = project.tasks || [];
  const childTasks = (project.children || []).flatMap(c => c.tasks || []);
  return [...own, ...childTasks];
};

/* ── notifications (per-user "seen" tracking via localStorage) ── */
const SEEN_STORAGE_KEY = 'abga_dashboard_seen';
const BASELINE_KEY = '__baseline';

const readSeenStore = () => {
  try {
    return JSON.parse(localStorage.getItem(SEEN_STORAGE_KEY)) || {};
  } catch {
    return {};
  }
};

const writeSeenStore = (store) => {
  try {
    localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* ignore quota / serialization errors */
  }
};

/** Record a first-login baseline so existing tasks don't all flag as "new". */
const ensureSeenBaseline = (userId) => {
  if (!userId) return;
  const store = readSeenStore();
  const userSeen = store[userId] || {};
  if (!userSeen[BASELINE_KEY]) {
    userSeen[BASELINE_KEY] = new Date().toISOString();
    store[userId] = userSeen;
    writeSeenStore(store);
  }
};

/** Last time the user viewed a project; falls back to the global baseline. */
const getProjectSeenAt = (userId, projectId) => {
  if (!userId) return null;
  const userSeen = readSeenStore()[userId] || {};
  return userSeen[projectId] || userSeen[BASELINE_KEY] || null;
};

const setProjectSeenAt = (userId, projectId, iso) => {
  if (!userId) return;
  const store = readSeenStore();
  const userSeen = store[userId] || {};
  userSeen[projectId] = iso;
  if (!userSeen[BASELINE_KEY]) userSeen[BASELINE_KEY] = iso;
  store[userId] = userSeen;
  writeSeenStore(store);
};

/** Resolve the root (sidebar-level) project id for any project or subproject id. */
const findRootProjectId = (projects, id) => {
  for (const p of projects) {
    if (p.id === id) return p.id;
    if (p.children?.some(c => c.id === id)) return p.id;
  }
  return id;
};

/**
 * Count unseen tasks per root project. A task is "new" when it was created by
 * another user after the current user last viewed that project.
 */
const computeNotificationsMap = (projects, userId) => {
  const map = {};
  if (!userId) return map;
  for (const root of projects) {
    const seenAt = getProjectSeenAt(userId, root.id);
    const seenTime = seenAt ? new Date(seenAt).getTime() : 0;
    let count = 0;
    for (const task of getAllTasks(root)) {
      if (!task.createdAt) continue;
      if (task.createdBy && task.createdBy === userId) continue;
      if (new Date(task.createdAt).getTime() > seenTime) count += 1;
    }
    map[root.id] = count;
  }
  return map;
};

/**
 * Latest activity timestamp (ms) per root project, across ALL users.
 * Uses the most recent of updatedAt / createdAt over the project's own and
 * child tasks. Drives dashboard ordering regardless of who made the change.
 */
const computeActivityMap = (projects) => {
  const map = {};
  for (const root of projects) {
    let latest = root.createdAt ? new Date(root.createdAt).getTime() : 0;
    for (const task of getAllTasks(root)) {
      const ts = task.updatedAt || task.createdAt;
      if (!ts) continue;
      const time = new Date(ts).getTime();
      if (time > latest) latest = time;
    }
    map[root.id] = latest;
  }
  return map;
};

/**
 * Map of root projectId -> true when a task was *updated* (not just created)
 * since the current user last viewed that project. Drives the sidebar
 * "unseen update" pulse, which clears once the user opens the project.
 */
const computeStatusUpdatesMap = (projects, userId) => {
  const map = {};
  if (!userId) return map;
  for (const root of projects) {
    const seenAt = getProjectSeenAt(userId, root.id);
    const seenTime = seenAt ? new Date(seenAt).getTime() : 0;
    let hasUpdate = false;
    for (const task of getAllTasks(root)) {
      if (!task.updatedAt || !task.createdAt) continue;
      const updated = new Date(task.updatedAt).getTime();
      const created = new Date(task.createdAt).getTime();
      // updated > created => it was edited after creation (e.g. status change)
      if (updated > created && updated > seenTime) { hasUpdate = true; break; }
    }
    map[root.id] = hasUpdate;
  }
  return map;
};

export const STATUS_LABELS = { todo: 'To Do', inprogress: 'In Progress', done: 'Done' };

/** Map of taskId -> { task, project, rootId, rootTitle } across the whole tree. */
const flattenTasksWithProject = (projects) => {
  const map = new Map();
  for (const root of projects) {
    const visit = (proj) => {
      for (const t of proj.tasks || []) {
        map.set(t.id, { task: t, project: proj, rootId: root.id, rootTitle: root.title });
      }
    };
    visit(root);
    for (const child of root.children || []) visit(child);
  }
  return map;
};

/**
 * Compare two project trees and return a list of change entries (created,
 * updated, deleted tasks). Used by polling to surface what other admins did.
 */
const diffChanges = (oldProjects, newProjects, currentUserId) => {
  const oldMap = flattenTasksWithProject(oldProjects);
  const newMap = flattenTasksWithProject(newProjects);
  const changes = [];
  const stamp = Date.now();
  const nowIso = new Date().toISOString();

  const base = (info) => ({
    projectId: info.project.id,
    rootProjectId: info.rootId,
    projectTitle: info.project.title,
    projectColor: info.project.color,
  });

  // Created tasks (skip ones the current user created — already known locally).
  for (const [id, info] of newMap) {
    if (oldMap.has(id)) continue;
    const t = info.task;
    if (currentUserId && t.createdBy === currentUserId) continue;
    changes.push({
      id: `created-${id}-${stamp}`,
      type: 'task_created',
      taskTitle: t.title,
      userName: t.createdByName || 'Someone',
      timestamp: t.createdAt || nowIso,
      ...base(info),
    });
  }

  // Deleted tasks.
  for (const [id, info] of oldMap) {
    if (newMap.has(id)) continue;
    changes.push({
      id: `deleted-${id}-${stamp}`,
      type: 'task_deleted',
      taskTitle: info.task.title,
      userName: '',
      timestamp: nowIso,
      ...base(info),
    });
  }

  // Updated tasks (status change is the meaningful, user-visible change).
  for (const [id, info] of newMap) {
    const old = oldMap.get(id);
    if (!old) continue;
    if (old.task.status !== info.task.status) {
      changes.push({
        id: `updated-${id}-${stamp}`,
        type: 'task_updated',
        taskTitle: info.task.title,
        userName: info.task.createdByName || '',
        fromStatus: old.task.status,
        toStatus: info.task.status,
        timestamp: nowIso,
        ...base(info),
      });
    }
  }

  return changes;
};

/** Group a diff cycle's changes into concise toast messages, one per project/type. */
const summarizeChanges = (changes) => {
  const stamp = Date.now();
  const byProject = new Map();
  for (const c of changes) {
    if (!byProject.has(c.rootProjectId)) {
      byProject.set(c.rootProjectId, {
        projectId: c.rootProjectId, projectTitle: c.projectTitle,
        projectColor: c.projectColor, created: [], updated: [], deleted: [],
      });
    }
    const g = byProject.get(c.rootProjectId);
    if (c.type === 'task_created') g.created.push(c);
    else if (c.type === 'task_updated') g.updated.push(c);
    else if (c.type === 'task_deleted') g.deleted.push(c);
  }

  const toasts = [];
  for (const [key, g] of byProject) {
    if (g.created.length) {
      const names = [...new Set(g.created.map(c => c.userName).filter(Boolean))];
      const who = names.length === 1 ? names[0] : names.length > 1 ? 'Your team' : 'Someone';
      const n = g.created.length;
      toasts.push({
        id: `toast-c-${key}-${stamp}`,
        projectId: g.projectId, projectColor: g.projectColor,
        message: `${who} added ${n} new task${n > 1 ? 's' : ''} to ${g.projectTitle}`,
      });
    }
    if (g.updated.length) {
      const n = g.updated.length;
      const message = n === 1 && g.updated[0].toStatus
        ? `"${g.updated[0].taskTitle}" moved to ${STATUS_LABELS[g.updated[0].toStatus] || g.updated[0].toStatus} in ${g.projectTitle}`
        : `${n} task${n > 1 ? 's' : ''} updated in ${g.projectTitle}`;
      toasts.push({ id: `toast-u-${key}-${stamp}`, projectId: g.projectId, projectColor: g.projectColor, message });
    }
    if (g.deleted.length) {
      const n = g.deleted.length;
      toasts.push({
        id: `toast-d-${key}-${stamp}`,
        projectId: g.projectId, projectColor: g.projectColor,
        message: `${n} task${n > 1 ? 's' : ''} removed from ${g.projectTitle}`,
      });
    }
  }
  return toasts;
};

const POLL_INTERVAL_VISIBLE_MS = 10000;
const POLL_INTERVAL_HIDDEN_MS = 30000;
const ACTIVITY_LOG_MAX = 30;
const PULSE_DURATION_MS = 3000;
export const LIVE_HEADLINE_MS = 60000;

/** Short human-readable line for a live activity entry (card headline / strip). */
export const formatActivityHeadline = (a) => {
  if (!a) return '';
  if (a.type === 'task_created') {
    return `${a.userName || 'Someone'} added "${a.taskTitle}"`;
  }
  if (a.type === 'task_updated') {
    return `"${a.taskTitle}" → ${STATUS_LABELS[a.toStatus] || a.toStatus}`;
  }
  return `"${a.taskTitle}" removed`;
};

/** Latest fresh activity per root project (within maxAgeMs). */
export const getLatestActivityByProject = (activityLog, maxAgeMs = LIVE_HEADLINE_MS, now = Date.now()) => {
  const map = {};
  for (const a of activityLog || []) {
    const rootId = a.rootProjectId;
    if (!rootId || map[rootId]) continue;
    const ts = a.timestamp ? new Date(a.timestamp).getTime() : 0;
    if (!ts || now - ts > maxAgeMs) continue;
    map[rootId] = a;
  }
  return map;
};

/** Flatten tasks across all root projects and subprojects, optionally filter by status. */
export const collectTasksFromProjects = (projects, statusFilter = null) => {
  const items = [];
  for (const root of projects) {
    const pushTasks = (proj) => {
      for (const task of proj.tasks || []) {
        if (statusFilter && task.status !== statusFilter) continue;
        items.push({
          ...task,
          projectId: proj.id,
          projectTitle: proj.title,
          projectColor: proj.color,
          rootProjectId: root.id,
          rootProjectTitle: root.title,
          isSubproject: proj.id !== root.id,
        });
      }
    };
    pushTasks(root);
    for (const child of root.children || []) pushTasks(child);
  }
  return items;
};

const updateProjectInTree = (projects, id, updater) =>
  projects.map(p => {
    if (p.id === id) return updater(p);
    if (p.children?.some(c => c.id === id)) {
      return {
        ...p,
        children: p.children.map(c => (c.id === id ? updater(c) : c)),
      };
    }
    return p;
  });

const removeProjectFromTree = (projects, id) => {
  if (projects.some(p => p.id === id)) {
    return projects.filter(p => p.id !== id);
  }
  return projects.map(p => ({
    ...p,
    children: p.children?.filter(c => c.id !== id) ?? [],
  }));
};

const insertProjectInTree = (projects, decorated) => {
  if (!decorated.parentId) {
    return [decorated, ...projects];
  }
  return projects.map(p =>
    p.id === decorated.parentId
      ? { ...p, children: [...(p.children || []), decorated] }
      : p
  );
};

/* ── provider ─────────────────────────────────────────── */
export function AppProvider({ children }) {
  const [isLoggedIn, setIsLoggedIn] = useState(hasAuthSession());
  const [user, setUser]             = useState(null);
  const [projects, setProjects]     = useState([]);
  const [loading, setLoading]       = useState(hasAuthSession());
  const [authError, setAuthError]   = useState('');
  const [notificationsMap, setNotificationsMap] = useState({});
  const [statusUpdatesMap, setStatusUpdatesMap] = useState({});
  const [activityMap, setActivityMap] = useState({});
  const [activityLog, setActivityLog] = useState([]);
  const [activityToasts, setActivityToasts] = useState([]);
  const [recentlyChangedProjects, setRecentlyChangedProjects] = useState(() => new Set());

  // Refs let the stable loadProjects callback see the latest snapshot/user.
  const projectsRef = useRef([]);
  const userRef = useRef(null);
  useEffect(() => { projectsRef.current = projects; }, [projects]);
  useEffect(() => { userRef.current = user; }, [user]);

  const loadProjects = useCallback(async ({ diff = false } = {}) => {
    const data = await apiGetProjects();
    const decorated = data.map(decorateProject);

    if (diff && projectsRef.current.length) {
      const changes = diffChanges(projectsRef.current, decorated, userRef.current?.id);
      if (changes.length) {
        setActivityLog(prev => [...changes, ...prev].slice(0, ACTIVITY_LOG_MAX));
        setActivityToasts(prev => [...prev, ...summarizeChanges(changes)]);
        setRecentlyChangedProjects(() => {
          const next = new Set();
          changes.forEach(c => next.add(c.rootProjectId));
          return next;
        });
      }
    }

    setProjects(decorated);
  }, []);

  const dismissActivityToast = useCallback((id) => {
    setActivityToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const clearActivityLog = useCallback(() => setActivityLog([]), []);

  useEffect(() => {
    let cancelled = false;
    const restore = async () => {
      if (!hasAuthSession()) {
        setLoading(false);
        return;
      }
      try {
        const me = await apiGetMe();
        if (cancelled) return;
        setUser(decorateUser(me));
        await loadProjects();
        if (cancelled) return;
        setIsLoggedIn(true);
      } catch {
        clearAuthTokens();
        if (!cancelled) {
          setIsLoggedIn(false);
          setUser(null);
          setProjects([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    restore();
    return () => { cancelled = true; };
  }, [loadProjects]);

  /* ── activity ordering: recompute on any project/task change ── */
  useEffect(() => {
    setActivityMap(computeActivityMap(projects));
  }, [projects]);

  /* ── notifications: recompute whenever projects or user change ── */
  useEffect(() => {
    if (!user) {
      setNotificationsMap({});
      setStatusUpdatesMap({});
      return;
    }
    ensureSeenBaseline(user.id);
    setNotificationsMap(computeNotificationsMap(projects, user.id));
    setStatusUpdatesMap(computeStatusUpdatesMap(projects, user.id));
  }, [projects, user]);

  /* ── poll for changes made by other admins (faster when tab visible) ── */
  useEffect(() => {
    if (!isLoggedIn) return undefined;

    let intervalId;
    const tick = () => { loadProjects({ diff: true }).catch(() => {}); };

    const schedule = () => {
      if (intervalId) clearInterval(intervalId);
      const ms = document.hidden ? POLL_INTERVAL_HIDDEN_MS : POLL_INTERVAL_VISIBLE_MS;
      intervalId = setInterval(tick, ms);
    };

    const onVisibility = () => {
      if (!document.hidden) tick();
      schedule();
    };

    schedule();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [isLoggedIn, loadProjects]);

  /* ── clear the "recently changed" pulse highlight after the animation ── */
  useEffect(() => {
    if (recentlyChangedProjects.size === 0) return undefined;
    const t = setTimeout(() => setRecentlyChangedProjects(new Set()), PULSE_DURATION_MS);
    return () => clearTimeout(t);
  }, [recentlyChangedProjects]);

  /** Mark a project (or its root) as seen, clearing its notification badge and live headlines. */
  const markProjectSeen = useCallback((projectId) => {
    if (!user || !projectId) return;
    const rootId = findRootProjectId(projects, projectId);
    setProjectSeenAt(user.id, rootId, new Date().toISOString());
    setNotificationsMap(computeNotificationsMap(projects, user.id));
    setStatusUpdatesMap(computeStatusUpdatesMap(projects, user.id));
    setActivityLog(prev => prev.filter(a => a.rootProjectId !== rootId));
    setRecentlyChangedProjects(prev => {
      if (!prev.has(rootId)) return prev;
      const next = new Set(prev);
      next.delete(rootId);
      return next;
    });
  }, [user, projects]);

  /* ── auth ───────────────────────────────────────────── */
  const login = async (email, password) => {
    setAuthError('');
    const { accessToken, refreshToken, user: apiUser } = await apiLogin(email, password);
    setAuthTokens(accessToken, refreshToken);
    setUser(decorateUser(apiUser));
    await loadProjects();
    setIsLoggedIn(true);
  };

  const logout = async () => {
    try {
      await apiLogout(getRefreshToken());
    } catch {
      // ignore — session is cleared locally regardless
    }
    clearAuthTokens();
    setIsLoggedIn(false);
    setUser(null);
    setProjects([]);
    setActivityLog([]);
    setActivityToasts([]);
    setRecentlyChangedProjects(new Set());
    setStatusUpdatesMap({});
  };

  /* ── projects ───────────────────────────────────────── */
  const addProject = async (data) => {
    const created = await apiCreateProject(data);
    const decorated = decorateProject(created);
    setProjects(prev => insertProjectInTree(prev, decorated));
    return decorated.id;
  };

  const updateProject = async (id, changes) => {
    const prev = projects;
    setProjects(p => updateProjectInTree(p, id, x => ({ ...x, ...changes })));
    try {
      const updated = await apiUpdateProject(id, changes);
      setProjects(p =>
        updateProjectInTree(p, id, node => ({
          ...node,
          title: updated.title,
          description: updated.description ?? '',
          color: updated.color,
          stagingUrl: updated.stagingUrl ?? '',
          productionUrl: updated.productionUrl ?? '',
        }))
      );
    } catch (e) {
      setProjects(prev);
      throw e;
    }
  };

  const deleteProject = async (id) => {
    const prev = projects;
    setProjects(p => removeProjectFromTree(p, id));
    try {
      await apiDeleteProject(id);
    } catch (e) {
      setProjects(prev);
      throw e;
    }
  };

  /* ── tasks ──────────────────────────────────────────── */
  const addTask = async (projectId, taskData) => {
    const payload = {
      title: taskData.title,
      desc: taskData.desc ?? '',
      status: taskData.status ?? 'todo',
      priority: taskData.priority ?? 'medium',
    };
    const created = await apiCreateTask(projectId, payload);
    const task = decorateTask(created);
    setProjects(prev =>
      updateProjectInTree(prev, projectId, p => ({
        ...p,
        tasks: [...p.tasks, task],
      }))
    );
  };

  const updateTask = async (projectId, taskId, changes) => {
    const prev = projects;
    const now = new Date().toISOString();
    // The current user is editing this project, so treat it as "seen" — this
    // keeps their own status changes from triggering the sidebar update pulse.
    const rootId = user ? findRootProjectId(projects, projectId) : null;
    if (user) setProjectSeenAt(user.id, rootId, now);
    setProjects(p =>
      updateProjectInTree(p, projectId, proj => ({
        ...proj,
        tasks: proj.tasks.map(t => (t.id === taskId ? { ...t, ...changes, updatedAt: now } : t)),
      }))
    );
    try {
      const updated = await apiUpdateTask(projectId, taskId, changes);
      if (updated?.updatedAt) {
        // Bump "seen" to the server timestamp so it isn't flagged as unseen.
        if (user) setProjectSeenAt(user.id, rootId, updated.updatedAt);
        setProjects(p =>
          updateProjectInTree(p, projectId, proj => ({
            ...proj,
            tasks: proj.tasks.map(t => (t.id === taskId ? { ...t, updatedAt: updated.updatedAt } : t)),
          }))
        );
      }
    } catch (e) {
      setProjects(prev);
      throw e;
    }
  };

  const deleteTask = async (projectId, taskId) => {
    const prev = projects;
    setProjects(p =>
      updateProjectInTree(p, projectId, proj => ({
        ...proj,
        tasks: proj.tasks.filter(t => t.id !== taskId),
      }))
    );
    try {
      await apiDeleteTask(projectId, taskId);
    } catch (e) {
      setProjects(prev);
      throw e;
    }
  };

  /* ── notes ──────────────────────────────────────────── */
  const addNote = async (projectId, text) => {
    const created = await apiCreateNote(projectId, text);
    const note = decorateNote(created);
    setProjects(prev =>
      updateProjectInTree(prev, projectId, p => ({
        ...p,
        notes: [...p.notes, note],
      }))
    );
  };

  const deleteNote = async (projectId, noteId) => {
    const prev = projects;
    setProjects(p =>
      updateProjectInTree(p, projectId, proj => ({
        ...proj,
        notes: proj.notes.filter(n => n.id !== noteId),
      }))
    );
    try {
      await apiDeleteNote(projectId, noteId);
    } catch (e) {
      setProjects(prev);
      throw e;
    }
  };

  return (
    <AppContext.Provider value={{
      isLoggedIn, loading, authError,
      login, logout,
      user: user ?? { name: '', email: '', avatar: '' },
      projects,
      notificationsMap, statusUpdatesMap, activityMap, markProjectSeen,
      activityLog, activityToasts, recentlyChangedProjects,
      dismissActivityToast, clearActivityLog,
      addProject, updateProject, deleteProject,
      addTask, updateTask, deleteTask,
      addNote, deleteNote,
    }}>
      {children}
    </AppContext.Provider>
  );
}

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be inside AppProvider');
  return ctx;
};
